import React, { useState } from 'react';

import {
  ActionIcon,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
  createStyles,
} from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconFilterX } from '@tabler/icons-react';
import DownloadCSV from 'components/DownloadCSV';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthleteEvents from 'hooks/athleteEvent/useAthleteEvents';
import useTotalAttended from 'hooks/athleteEvent/useTotalAttended';
import useTotalReserved from 'hooks/athleteEvent/useTotalReserved';
import { AthleteEvent } from 'types';

import { ReservationType, SearchFilters, WeekdayType } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xs,
    display: 'grid',
    gridTemplateColumns: 'auto repeat(4, minmax(0, 1fr)) min-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title weekday reservationType dateRange eventTypes clear download attendanceCard"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto auto min-content min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title clear download'
        'dateRange eventTypes eventTypes eventTypes'
        'weekday weekday reservationType reservationType'
        'attendanceCard attendanceCard attendanceCard attendanceCard'
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto auto min-content min-content',
      gridTemplateRows: 'repeat(auto, 6)',
      gridTemplateAreas: `
        'title title clear download'
        'dateRange dateRange dateRange dateRange'
        'weekday weekday weekday weekday'
        'reservationType reservationType reservationType reservationType'
        'eventTypes eventTypes eventTypes eventTypes'
        'attendanceCard attendanceCard attendanceCard attendanceCard'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  weekday: {
    gridArea: 'weekday',
  },
  reservationType: {
    gridArea: 'reservationType',
  },
  dateRange: {
    gridArea: 'dateRange',
  },
  eventTypes: {
    gridArea: 'eventTypes',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
  },
  attendanceCard: {
    gridArea: 'attendanceCard',
    display: 'flex',
    width: 'fit-content',
    gap: theme.spacing.xl,
  },
}));

const Weekdays: Array<{ label: WeekdayType['weekDay']; value: WeekdayType['day'] }> = [
  { value: '1', label: 'Sunday' },
  { value: '2', label: 'Monday' },
  { value: '3', label: 'Tuesday' },
  { value: '4', label: 'Wednesday' },
  { value: '5', label: 'Thursday' },
  { value: '6', label: 'Friday' },
  { value: '7', label: 'Saturday' },
];

const BulkAddOptions: Array<{ label: ReservationType['label']; value: ReservationType['type'] }> = [
  { value: 'true', label: 'Bulk Add' },
  { value: 'false', label: 'Not Bulk Add' },
];

interface IAthleteAttendanceFiltersProps {
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  initialSearchFiltersValue: SearchFilters;
  orderingValue: string;
  permission?: boolean;
  timeType?: 'past' | 'upcoming';
  limit?: string;
  defaultEndDate?: string;
}

export const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: false,
  headers: ['Name', 'Date', 'Time', 'Reserved', 'Attended'],
};

const today = new Date();

const AthleteAttendanceFilters = ({
  searchFilters,
  setSearchFilters,
  initialSearchFiltersValue,
  orderingValue,
  defaultEndDate,
  timeType,
  limit,
}: IAthleteAttendanceFiltersProps): JSX.Element => {
  const { classes } = useStyles();
  const { dateFormat } = useLocalization();

  const { data: totalReserved } = useTotalReserved({
    ...searchFilters,
    startDateRange:
      searchFilters.date && searchFilters.date[0]
        ? dayjs(searchFilters.date[0]).format('YYYY-MM-DD')
        : undefined,
    endDateRange:
      searchFilters.date && searchFilters.date[1]
        ? dayjs(searchFilters.date[1]).format('YYYY-MM-DD')
        : dayjs(today).format('YYYY-MM-DD'),
    type: 'past',
  });
  const { data: totalAttended } = useTotalAttended({
    ...searchFilters,
    startDateRange:
      searchFilters.date && searchFilters.date[0]
        ? dayjs(searchFilters.date[0]).format('YYYY-MM-DD')
        : undefined,
    endDateRange:
      searchFilters.date && searchFilters.date[1]
        ? dayjs(searchFilters.date[1]).format('YYYY-MM-DD')
        : dayjs(today).format('YYYY-MM-DD'),
    type: 'past',
  });

  const [eventTypes, setEventTypes] = useState<IEventTypeMultiSelectAutoComplete[]>([]);

  const dataToRow = (d: AthleteEvent): { [key: string]: any } => ({
    Name: d.name,
    Date: dayjs(d.startDate).format(dateFormat),
    Time: dayjs(`${d.startDate} ${d.startTime}`).format('h:mm A'),
    Reserved: d.isReserved ? 'Yes' : 'No',
    Attended: d.isCheckedIn && dayjs(`${d.startDate} ${d.startTime}`).isBefore(dayjs(today)) ? 'Yes' : 'No',
  });

  const handleDateChange = (value: DatesRangeValue): void => {
    setSearchFilters(prev => ({ ...prev, date: value }));
  };
  const handleEventsChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    setEventTypes(values);
    setSearchFilters(prev => ({ ...prev, events: values.map(v => v.eventTypeId) }));
  };

  const handleWeekdaysChange = (values: WeekdayType['day'][]): void => {
    setSearchFilters(prev => ({ ...prev, weekdays: values }));
  };

  const handleReservationTypeChange = (value: ReservationType['type'] | null): void => {
    setSearchFilters(prev => ({ ...prev, isBulkAdd: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
    setEventTypes([]);
  };

  return (
    <div className={classes.header}>
      <Title order={2} className={classes.title}>
        Attendance
      </Title>
      <MultiSelect
        value={searchFilters.weekdays ?? []}
        onChange={event => handleWeekdaysChange(event as WeekdayType['day'][])}
        data={Weekdays}
        className={classes.weekday}
        placeholder='Select weekdays'
      />
      <Select
        value={searchFilters.isBulkAdd ?? null}
        onChange={event => handleReservationTypeChange(event as ReservationType['type'])}
        data={BulkAddOptions}
        className={classes.reservationType}
        placeholder='Select reservation type'
        clearable
      />
      <DatePickerInput
        className={classes.dateRange}
        value={searchFilters.date}
        onChange={handleDateChange}
        valueFormat='YYYY MMM DD'
        type='range'
        clearable
        {...{ placeholder: 'Select date range' }}
        maxDate={today}
        allowSingleDateInRange
      />
      <EventTypeMultiSelectAutoComplete
        className={classes.eventTypes}
        handleChange={handleEventsChange}
        values={eventTypes}
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterX />
        </ActionIcon>
      </Tooltip>

      <DownloadCSV
        useList={useAthleteEvents}
        dataToRow={dataToRow}
        hookOptions={{
          ...searchFilters,
          startDateRange:
            searchFilters.date && searchFilters.date[0]
              ? dayjs(searchFilters.date[0]).format('YYYY-MM-DD')
              : undefined,
          endDateRange:
            searchFilters.date && searchFilters.date[1]
              ? dayjs(searchFilters.date[1]).format('YYYY-MM-DD')
              : defaultEndDate,
          hideWaitlist: false,
          ordering: orderingValue,
          type: timeType,
          isBulkAdd: searchFilters.isBulkAdd ? searchFilters.isBulkAdd === 'true' : undefined,
        }}
        limit={limit}
        csvOptions={csvOptions}
        className={classes.download}
        reportType='athlete-attendance-export'
      />
      <Paper shadow='sm' radius='lg' p='xs' withBorder className={classes.attendanceCard}>
        <Stack sx={{ gap: 0 }} w='max-content'>
          <Text>Reserved Total</Text>
          <Text fw={700} fz='xl'>
            {totalReserved?.data.reservedTotalAmount}
          </Text>
        </Stack>
        <Stack sx={{ gap: 0 }} w='max-content'>
          <Text>Attended Total</Text>
          <Text fw={700} fz='xl'>
            {totalAttended?.data.attendedTotalAmount}
          </Text>
        </Stack>
      </Paper>
    </div>
  );
};

export default AthleteAttendanceFilters;
